import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../app/hooks";
import { PriceTypeQuantity } from "../data/interfaces/types";
import bestAvailableModelSlice from "../data/slices/best-available-model-slice";
import miniCartModelSlice from "../data/slices/mini-cart-slicer";

interface PriceTypeQuantityProps {
    ptQuantity: PriceTypeQuantity;
    index: number;
}

export const BAPriceTypeQuantity: React.FC<PriceTypeQuantityProps> = ({ ptQuantity, index }) => {
    const bestAvailableText = useAppSelector((state) => state.bestAvailableText);
    const bestAvailableModel = useAppSelector((state) => state.bestAvailableModel);
    const [currentPriceTypeQuantities, setCurrentPriceTypeQuantities] = useState(bestAvailableModel.priceTypeQuantities);
    const [currentInputValue, setCurrentInputValue] = useState(ptQuantity.quantity);
    const [currentAmountValue, setCurrentAmountValue] = useState<number | string>(() =>
        ptQuantity.isPwyc ? ptQuantity.pwycMinAmount : ptQuantity.amount
    );


    const dispatch = useDispatch();
    const selectedZoneId = useAppSelector((state) => state.bestAvailableModel.selectedZoneId);
    const [currentSelectedZoneId, setCurrentSelectedZoneId] = useState(selectedZoneId);


    useEffect(() => { setCurrentPriceTypeQuantities(bestAvailableModel.priceTypeQuantities) }, [bestAvailableModel.priceTypeQuantities])
    useEffect(() => { setCurrentInputValue(ptQuantity.quantity) }, [ptQuantity.quantity])
    useEffect(() => {
        if (ptQuantity.isPwyc) {
            setCurrentAmountValue(prev => prev ?? ptQuantity.pwycMinAmount);
        } else {
            setCurrentAmountValue(ptQuantity.amount);
        }
    }, [ptQuantity.amount, ptQuantity.isPwyc, ptQuantity.pwycMinAmount]);
    useEffect(() => { setCurrentSelectedZoneId(selectedZoneId) }, [selectedZoneId])



    const currencyFormatter = (amount: number): string => {
        var formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        });

        return formatter.format(amount);
    }

    const updatePriceTypeQuantity = (newQuantity: number) => {
        const numericAmount = Number(currentAmountValue);
        const amountToUse = ptQuantity.isPwyc
            ? (!isNaN(numericAmount) && numericAmount >= ptQuantity.pwycMinAmount ? numericAmount : ptQuantity.pwycMinAmount)
            : ptQuantity.amount;

        let newPtQuantity: PriceTypeQuantity = {
            priceTypeId: ptQuantity.priceTypeId,
            priceTypeDescription: ptQuantity.priceTypeDescription,
            quantity: newQuantity,
            minQuantity: ptQuantity.minQuantity,
            maxQuantity: ptQuantity.maxQuantity,
            amount: amountToUse,
            totalAmount: amountToUse * newQuantity,
            zoneId: ptQuantity.zoneId,
            isDisabled: ptQuantity.isDisabled,
            isWheelchairAccessible: ptQuantity.isWheelchairAccessible,
            isPwyc: ptQuantity.isPwyc,
            pwycMinAmount: ptQuantity.pwycMinAmount,
            editableMinAmount: ptQuantity.editableMinAmount
        };

        dispatch(bestAvailableModelSlice.actions.updatePriceTypeQuantities(newPtQuantity));
        dispatch(miniCartModelSlice.actions.updateMiniCartState(newPtQuantity));

        if (newPtQuantity.isWheelchairAccessible) {
            dispatch(bestAvailableModelSlice.actions.updateShowWheelchairActivityType(newQuantity > 0));
        }
    };


    const updatePriceTypeAmount = (newAmount: number) => {
        let newPtQuantity: PriceTypeQuantity = {
            priceTypeId: ptQuantity.priceTypeId,
            priceTypeDescription: ptQuantity.priceTypeDescription,
            quantity: ptQuantity.quantity,
            minQuantity: ptQuantity.minQuantity,
            maxQuantity: ptQuantity.maxQuantity,
            amount: newAmount,
            totalAmount: ptQuantity.quantity * newAmount,
            zoneId: ptQuantity.zoneId,
            isDisabled: ptQuantity.isDisabled,
            isWheelchairAccessible: ptQuantity.isWheelchairAccessible,
            isPwyc: ptQuantity.isPwyc,
            pwycMinAmount: ptQuantity.pwycMinAmount,
            editableMinAmount: ptQuantity.editableMinAmount
        }
        dispatch(bestAvailableModelSlice.actions.updatePriceTypeQuantities(newPtQuantity));
        dispatch(miniCartModelSlice.actions.updateMiniCartState(newPtQuantity));

        if (newPtQuantity.isWheelchairAccessible) {
            if (newPtQuantity.quantity > 0) {
                dispatch(bestAvailableModelSlice.actions.updateShowWheelchairActivityType(true));
            }
            else {
                dispatch(bestAvailableModelSlice.actions.updateShowWheelchairActivityType(false));
            }
        }
    }

    const updateInputValue = (evt) => {
        let newNum = Number(evt.target.value);
        if (!Number.isInteger(newNum) || isNaN(newNum)) {
            return;
        } else if (newNum < 0 || newNum > 9999) {
            return;
        }

        if (newNum >= ptQuantity.maxQuantity) {
            newNum = ptQuantity.maxQuantity;
        } else if (newNum < 0) {
            newNum = 0;
        }

        setCurrentInputValue(newNum);
        updatePriceTypeQuantity(newNum);
    }

    const handleAmountBlur = () => {
        const numValue = Number(currentAmountValue);
        if (currentAmountValue === "" || isNaN(numValue) || numValue < ptQuantity.pwycMinAmount) {
            setCurrentAmountValue(ptQuantity.pwycMinAmount.toFixed(2));
            updatePriceTypeAmount(ptQuantity.pwycMinAmount);
        } else {
            setCurrentAmountValue(numValue.toFixed(2));
        }
    };


    const updateAmountValue = (evt) => {
        const value = evt.target.value;

        if (value === "") {
            setCurrentAmountValue("");
            return;
        }

        // Allow digits with an optional decimal point and up to 2 decimal places (e.g. "99", "99.", "99.9", "99.95").
        if (!/^\d*\.?\d{0,2}$/.test(value)) return;

        setCurrentAmountValue(value);

        const newAmount = Number(value);
        if (!isNaN(newAmount) && newAmount >= ptQuantity.pwycMinAmount) {
            updatePriceTypeAmount(newAmount);
        }
    };


    const decreaseQuantity = () => {
        let newValue = parseInt(currentInputValue.toString()) - 1;
        setCurrentInputValue(newValue);
        updatePriceTypeQuantity(newValue);
    }

    const increaseQuantity = () => {
        let newValue = parseInt(currentInputValue.toString()) + 1;
        setCurrentInputValue(newValue);
        updatePriceTypeQuantity(newValue);
    }

    const disableIncreaseButton = () => {
        const currentGrantTotalwithAllPt = currentPriceTypeQuantities.reduce((total, pt) => total + pt.quantity, 0);
        if (bestAvailableModel.isExchange)
            return currentGrantTotalwithAllPt >= ptQuantity.maxQuantity;
        return currentInputValue >= ptQuantity.maxQuantity;
    }

    const renderPriceTypes = () => {
        return (
            <div className="ace-layout ace-layout-2-eq ace-bestavailable-price">
                <div className="stack-sm">
                    <p className="bold">{ptQuantity.priceTypeDescription}</p>
                    {
                        ptQuantity.isPwyc ?
                            <div>
                                <label htmlFor={`pwyc_${ptQuantity.priceTypeId}_${ptQuantity.zoneId}`}>{bestAvailableText.pwycAmountTextboxLabel}</label>
                                <div className="currency-input">
                                    <input
                                        id={`pwyc_${ptQuantity.priceTypeId}_${ptQuantity.zoneId}`}
                                        data-amount
                                        type="text"
                                        inputMode="decimal"
                                        value={currentAmountValue}
                                        onChange={updateAmountValue}
                                        onBlur={handleAmountBlur}
                                    />
                                </div>
                            </div>
                            :
                            <p data-amount>{currencyFormatter(ptQuantity.amount)}</p>
                    }
                    {
                        ptQuantity.isDisabled &&
                        <p className="bold error-message">{bestAvailableText.zoneSoldOutText}</p>
                    }
                </div>
                <div className="ace-bestavailable-price-stepper">
                    <div className="stepper">
                        <button className="stepper-btn stepper-btn-down" type="button" onClick={decreaseQuantity} disabled={currentInputValue <= 0}>
                            <span className="visually-hidden">decrease</span>
                        </button>
                        <input className="stepper-input" type="number" value={Number(currentInputValue).toString()} min={ptQuantity.minQuantity} pattern="[0-9]{3}" max={ptQuantity.maxQuantity} onChange={updateInputValue} disabled={disableIncreaseButton() && currentInputValue === 0} />
                        <button className="stepper-btn stepper-btn-up" type="button" onClick={increaseQuantity} disabled={disableIncreaseButton()}>
                            <span className="visually-hidden">increase</span>
                        </button>
                    </div>
                    <div className="ace-bestavailable-amount">
                        <p data-total-amount className="bold" id="itemTotal_@i">
                            {currencyFormatter(ptQuantity.totalAmount)}
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <li key={index} className="ace-bestavailable-price-item">
            {
                bestAvailableModel.isGeneralAdmission &&
                renderPriceTypes()
            }
            {
                !bestAvailableModel.isGeneralAdmission && ptQuantity.zoneId == currentSelectedZoneId &&
                renderPriceTypes()
            }
        </li>
    )
}